import React from 'react';
import { Spin } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import * as atoms from '../atom';
import { Loading } from '../../common/Loading';
import { Retry } from '../../common/Retry';
import { DataSourceSelector } from '../DataSourceSelector';
import { DimensionCategory } from '../DimensionCategory';
import SavedFilterList from '../SavedFilterList';
import { FilterPanel } from '../FilterPanel';
import { useResetState, useFilterPanelVisible } from '../hooks';
import HelpRedirect from '@src/components/common/HelpRedirect';
import { MANAGING_FILTERS_HELP } from '@src/utils/help/links';
import * as Styled from './styled';
import type * as Types from '../../../types';

export interface ManagementProps extends atoms.ManagementStoreProps {
  savedFilters: Types.SavedFilter[];
}

const Main = (props: ManagementProps) => {
  const {
    visible = false,
    isFilterMetaLoading = false,
    isFilterMetaError = false,
    isSavedFiltersLoading = false,
    isSavedFiltersError = false,
    disabledDataSources = [],
    savedFilters = [],
  } = props;
  const callbacks = useAtomValue(atoms.callbacks);
  const { isVisible: isFilterPanelVisible } = useFilterPanelVisible();
  const { reset } = useResetState();
  const [asyncLoadingMask, setAsyncLoadingMask] = useAtom(atoms.asyncLoadingMask);

  const handleOnClose = () => {
    setAsyncLoadingMask(false);
    reset();
    callbacks.onClose?.();
  };

  const renderContent = () => {
    if (isFilterMetaError) {
      return <Retry onClick={() => callbacks.onFilterMetaRetry?.()} />;
    }

    return (
      <Styled.ContentContainer>
        {!isFilterPanelVisible && (
          <Styled.LeftPanel>
            <DimensionCategory />
          </Styled.LeftPanel>
        )}
        <Styled.RightPanel>
          <DataSourceSelector disabledDataSources={disabledDataSources} />
          <FilterPanel />
          {!isFilterPanelVisible && !isFilterMetaLoading && (
            <SavedFilterList
              savedFilters={savedFilters}
              isSavedFiltersLoading={isSavedFiltersLoading}
              isSavedFiltersError={isSavedFiltersError}
            />
          )}
        </Styled.RightPanel>
        {isFilterMetaLoading && <Loading />}
      </Styled.ContentContainer>
    );
  };

  return (
    <Styled.Drawer
      open={visible}
      closeIcon={null}
      styles={{
        wrapper: {
          width: '85%',
          minWidth: '800px',
        },
        body: {
          backgroundColor: '#f6f6f6',
          padding: 0,
        },
      }}
      onClose={handleOnClose}
    >
      {renderContent()}
      <Styled.HelpRedirectWrapper>
        <HelpRedirect redirectUrl={MANAGING_FILTERS_HELP} />
      </Styled.HelpRedirectWrapper>
      <Styled.CloseIcon onClick={handleOnClose} />
      {!!asyncLoadingMask && (
        <Styled.LoadingMask>
          <Spin />
        </Styled.LoadingMask>
      )}
    </Styled.Drawer>
  );
};

export default React.memo(Main);
